import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDashboardFilters, formatPeriodForTimeRange, type DashboardScope } from '../contexts/DashboardFiltersContext';
import TimeRangeSelector, { CalendarIcon } from './TimeRangeSelector';
import DashboardSwitcher from './DashboardSwitcher';

const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const TeamIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CustomizeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const DoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const AddWidgetIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

export interface DashboardFilterBarProps {
  /** When set, show dashboard switcher (current name and save-as) */
  currentLayoutName?: string;
  onSaveAsDashboard?: () => void;
  /** Open dashboard settings (widgets, rename/delete dashboards) */
  onManageDashboards?: () => void;
  /** Reload dashboard layout from server */
  onRefresh?: () => void;
  /** Customize mode active (edit layout) */
  isCustomizing?: boolean;
  /** Toggle customize / edit mode */
  onCustomizeToggle?: () => void;
  /** Open dashboard settings (add widgets, groups) */
  onOpenSettings?: () => void;
}

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  currentLayoutName,
  onSaveAsDashboard,
  onManageDashboards,
  onRefresh,
  isCustomizing = false,
  onCustomizeToggle,
  onOpenSettings,
}) => {
  const { theme, colors } = useTheme();
  const isDark = theme === 'dark';
  const { timeRange, setTimeRange, scope, setScope } = useDashboardFilters();

  const filterBg = isDark ? 'bg-slate-800/50' : 'bg-slate-50/90';
  const filterBorder = isDark ? 'border-slate-600/80' : 'border-slate-200/80';
  const actionBtnBase = 'p-2 rounded-lg transition-colors flex items-center justify-center min-h-[40px] min-w-[40px] sm:min-h-[36px] sm:min-w-[36px] touch-manipulation';
  const actionBtnMuted = isDark
    ? 'text-slate-400 hover:text-slate-100 hover:bg-slate-700/60'
    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-200/80';

  return (
    <header
      className={`flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4 sm:gap-0 px-4 py-3.5 sm:px-6 sm:py-3 border-b ${
        isDark
          ? 'border-slate-700/60 bg-slate-900/50'
          : `${colors.border} bg-white/98 shadow-sm`
      }`}
    >
      {/* Left: Dashboard switcher */}
      {onSaveAsDashboard != null && (
        <div className="flex items-center shrink-0 sm:mr-6">
          <DashboardSwitcher
            currentLayoutName={currentLayoutName}
            onSaveAsClick={onSaveAsDashboard}
            onManageClick={onManageDashboards}
          />
        </div>
      )}

      {/* Center: Filters group (time + scope in one card) */}
      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 flex-1 min-w-0 rounded-xl border px-4 py-3 sm:py-2.5 ${filterBorder} ${filterBg}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className={`flex items-center gap-2 shrink-0 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>Time</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 touch-pan-x min-w-0">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>
            <span className={`text-sm tabular-nums ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {timeRange === 'all' ? '(No Time Range)' : `(Date Period: ${formatPeriodForTimeRange(timeRange)})`}
            </span>
          </div>
        </div>
        <div className={`hidden sm:block w-px h-6 self-stretch sm:self-center ${isDark ? 'bg-slate-600/80' : 'bg-slate-200'}`} aria-hidden />
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
          <span className={`text-xs font-semibold uppercase tracking-wider shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Scope
          </span>
          <div
            className={`inline-flex items-center rounded-lg border p-0.5 min-h-[40px] sm:min-h-0 ${
              isDark ? 'border-slate-600 bg-slate-700/40' : 'border-slate-200 bg-white shadow-sm'
            }`}
          >
            {(['myWork', 'team'] as DashboardScope[]).map((option) => {
              const isSelected = scope === option;
              const isMyWork = option === 'myWork';
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setScope(option)}
                  className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 min-h-[36px] sm:min-h-0 text-sm font-medium transition-all duration-150 ${
                    isSelected
                      ? isDark
                        ? 'bg-teal-500/25 text-teal-300 shadow-sm'
                        : 'bg-teal-600 text-white shadow-sm'
                      : isDark
                        ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-600/40'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {isMyWork ? <UserIcon className="w-4 h-4" /> : <TeamIcon className="w-4 h-4" />}
                  {isMyWork ? 'My Work' : 'Team'}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: Action toolbar */}
      <div className="flex items-center gap-0.5 sm:ml-4 shrink-0 self-center sm:self-auto">
        {onCustomizeToggle != null && (
          <button
            type="button"
            onClick={onCustomizeToggle}
            className={`inline-flex items-center justify-center rounded-lg min-h-[40px] min-w-[40px] sm:min-h-[36px] sm:min-w-[36px] p-2 transition-all duration-150 touch-manipulation ${
              isCustomizing
                ? isDark
                  ? 'bg-teal-500 text-white shadow-md hover:bg-teal-600'
                  : 'bg-teal-600 text-white shadow-md hover:bg-teal-700'
                : actionBtnMuted
            }`}
            title={isCustomizing ? 'Done editing' : 'Customize dashboard'}
            aria-label={isCustomizing ? 'Done editing' : 'Customize dashboard'}
          >
            {isCustomizing ? <DoneIcon className="w-4 h-4 shrink-0" /> : <CustomizeIcon className="w-4 h-4 shrink-0" />}
          </button>
        )}
        {onOpenSettings != null && (
          <button
            type="button"
            onClick={onOpenSettings}
            className={`${actionBtnBase} ${actionBtnMuted}`}
            title="Add widgets"
            aria-label="Add widgets"
          >
            <AddWidgetIcon className="w-4 h-4 shrink-0" />
          </button>
        )}
        {onRefresh != null && (
          <button type="button" onClick={onRefresh} className={`${actionBtnBase} ${actionBtnMuted}`} title="Refresh dashboard" aria-label="Refresh dashboard">
            <RefreshIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </header>
  );
};

export default DashboardFilterBar;
